"use client";

/**
 * Gift Ask Fulton PDP interaction — rendered by the Gift Product Section
 * block (`section.gift-form`, see GiftFormSection.tsx). Value props on the
 * left (block rich text, first-card fallback), the owner's price cards
 * stacked on the right as selectable radio tiles, and an "Add to cart" CTA
 * that stores the choice in the gift-cart cookie (src/lib/giftCart.ts) and
 * navigates to /cart, where the Cart Section owns the purchase form.
 *
 * Fully CMS-driven: options come from the attached cards' `price` relations
 * (live prices via `enrichSectionsWithPolarPrices`); cards without a
 * `polarProductId` are skipped. CMS badges are overridden here — the MIDDLE
 * card always carries "Most Popular" and is the default selection.
 */

import * as React from "react";
import { useRouter } from "next/navigation";

import { Blocks, ContentCard } from "@/types/cms";
import { Button } from "@/components/button/button/button";
import CardPrice from "@/components/cards/cardPrice/cardPrice";
import { captureEvent } from "@/lib/analytics";
import { writeGiftCart } from "@/lib/giftCart";
import { formatPolarPriceText, type ResolvedPrice } from "@/lib/polarPriceFormat";
import { renderPriceBody, renderValuePropsMarkdown } from "@/lib/richText";
import { cn } from "@/lib/utils";
import { readPriceAttrs } from "./priceAttrs";
import styles from "./GiftForm.module.css";

interface GiftOption {
  polarProductId: string;
  badge: string | null;
  card: ContentCard;
}

/** The purchasable cards (price relation with a `polarProductId`) — each
 *  renders as the STANDARD price card via `CardPrice variant="selectable"`.
 *  Free-tier / non-purchasable cards are skipped. */
export function giftOptionsFromCards(cards: ContentCard[]): GiftOption[] {
  const options: GiftOption[] = [];
  for (const card of Array.isArray(cards) ? cards : []) {
    const attrs = readPriceAttrs(card);
    const polarProductId = (attrs?.polarProductId ?? "").toString().trim();
    if (!polarProductId) continue;
    options.push({
      polarProductId,
      badge: (attrs?.badge ?? "").toString().trim() || null,
      card,
    });
  }
  return options;
}

const DEFAULT_CTA_LABEL = "Add to cart";
const DEFAULT_ONE_TIME_NOTE = "One-time purchase";

export interface GiftFormProps {
  /** The section's content cards, in CMS order. */
  cards: ContentCard[];
  /** CMS-editable CTA label; empty falls back to "Add to cart". */
  ctaLabel?: string | null;
  /** CMS-editable caption under the CTA; empty falls back to
   *  "One-time purchase". */
  oneTimeNote?: string | null;
  /** The block's Value Props rich text (headings = column title, bullet
   *  lists = props). Empty falls back to the first card's price value props
   *  under a "They'll get" header. */
  valueProps?: Blocks;
}

const GiftForm = ({ cards, ctaLabel, oneTimeNote, valueProps }: GiftFormProps) => {
  const router = useRouter();
  const options = React.useMemo(() => giftOptionsFromCards(cards), [cards]);

  // Badge override: CMS badges are ignored here — the MIDDLE card always
  // carries "Most Popular" (and is the default selection). Needs a true
  // middle, so 1- and 2-option lineups keep CMS badges/default behavior.
  const middleIndex = options.length >= 3 ? Math.floor(options.length / 2) : -1;

  const [selectedId, setSelectedId] = React.useState<string>(() => {
    if (middleIndex !== -1) return options[middleIndex].polarProductId;
    return options.find((o) => o.badge)?.polarProductId ?? options[0]?.polarProductId ?? "";
  });
  // Guards double-clicks while the /cart navigation is in flight.
  const [busy, setBusy] = React.useState(false);

  // An authored section with no purchasable cards renders nothing rather
  // than a broken picker (e.g. the editor forgot to attach the price cards).
  if (options.length === 0) return null;

  // Value props render once, left of the card stack (the selectable cards
  // themselves hide them). The block's own Value Props rich text wins
  // (headings = title, bullets = props); when it's empty, fall back to the
  // FIRST card's price value-props markdown under a "They'll get" header.
  const cmsValueProps = Array.isArray(valueProps) && valueProps.length > 0 ? valueProps : null;
  const firstAttrs = readPriceAttrs(options[0].card);
  const valuePropsMarkdown = (firstAttrs?.valueProps ?? "").toString().trim();
  const showValueProps = !!(cmsValueProps || valuePropsMarkdown);

  const handleAddToCart = () => {
    if (busy || !selectedId) return;
    const selected = options.find((o) => o.polarProductId === selectedId);
    if (!selected) return;
    setBusy(true);
    // Snapshot the display data the buyer just saw — the cart renders
    // straight from the cookie, no CMS lookup (see giftCart.ts).
    const attrs = readPriceAttrs(selected.card);
    const resolved = attrs?.resolvedPrice as ResolvedPrice | undefined;
    writeGiftCart({
      polarProductId: selectedId,
      planTitle: (attrs?.planTitle ?? "").toString().trim() || undefined,
      planSubtitle: (attrs?.planSubtitle ?? "").toString().trim() || undefined,
      price:
        (resolved ? formatPolarPriceText(resolved) : (attrs?.price ?? "").toString().trim()) ||
        undefined,
      strikeoutPrice: (attrs?.strikeoutPrice ?? "").toString().trim() || undefined,
    });
    captureEvent("gift_added_to_cart", { polar_product_id: selectedId });
    router.push("/cart");
  };

  return (
    <div className={styles.root}>
      <fieldset className={styles.optionsFieldset} disabled={busy} aria-label="Choose a gift">
        <div
          className={cn(
            styles.optionsLayout,
            showValueProps && styles.optionsLayoutSplit,
          )}
        >
          {showValueProps && (
            <div className={styles.valueProps}>
              {cmsValueProps ? (
                renderPriceBody(cmsValueProps, {
                  heading: styles.valuePropsHeading,
                  bulletList: styles.valuePropsList,
                  paragraph: styles.valuePropsParagraph,
                })
              ) : (
                <>
                  <h3 className={cn("title-xs", styles.valuePropsHeading)}>
                    They&apos;ll get
                  </h3>
                  {renderValuePropsMarkdown(valuePropsMarkdown, {
                    bulletList: styles.valuePropsList,
                    paragraph: styles.valuePropsParagraph,
                  })}
                </>
              )}
            </div>
          )}
          <div className={styles.cardsColumn}>
            <div className={styles.cardsStack} role="radiogroup" aria-label="Choose a gift">
              {options.map((option, idx) => (
                <CardPrice
                  key={option.polarProductId}
                  card={option.card}
                  variant="selectable"
                  selected={option.polarProductId === selectedId}
                  onSelect={() => setSelectedId(option.polarProductId)}
                  badgeOverride={
                    middleIndex === -1
                      ? undefined
                      : idx === middleIndex
                        ? "Most Popular"
                        : null
                  }
                />
              ))}
            </div>
            <Button
              type="button"
              variant="primary"
              size="lg"
              className={styles.addToCart}
              disabled={busy}
              onClick={handleAddToCart}
            >
              {(ctaLabel ?? "").trim() || DEFAULT_CTA_LABEL}
            </Button>
            {/* Said ONCE for the whole stack, under the CTA — the per-card
                "One-Time" cadence is suppressed on selectable cards as
                noise. CMS-editable via the block's One-Time Note field. */}
            <p className={cn("body-xs", "muted", styles.oneTimeNote)}>
              {(oneTimeNote ?? "").trim() || DEFAULT_ONE_TIME_NOTE}
            </p>
          </div>
        </div>
      </fieldset>
    </div>
  );
};

export default GiftForm;
